import { useTranslation } from 'react-i18next';
import { I18nManager, Platform } from 'react-native';
import { useCallback, useEffect } from 'react';
import { reloadNativeApp } from '../i18n/reload-native';
import { showLocaleSwitchOverlay } from '../i18n/locale-switch-overlay';
import {
  getPersistedLocaleAsync,
  persistLocalePreference,
  type SupportedLocale,
} from '../lib/app-preferences-storage';

export type { SupportedLocale } from '../lib/app-preferences-storage';

// Give the "Switching to <language>…" overlay time to mount and paint before we
// tear down the JS runtime, so the user sees the branded transition rather than
// an unexplained black screen during the native RTL reload. Two frames flush the
// commit; the short hold lets the fade settle and reads as a deliberate step.
async function waitForLocaleSwitchOverlay() {
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => setTimeout(resolve, 500));
}

export async function getPersistedLocale(): Promise<SupportedLocale | null> {
  return getPersistedLocaleAsync();
}

function applyWebDirection(locale: SupportedLocale) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const isRTL = locale === 'ar';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;
}

export function useLocale() {
  const { i18n } = useTranslation();

  const locale = (i18n.language as SupportedLocale) || 'en';
  const isRTL = locale === 'ar';

  // Sync web direction on mount and locale changes
  useEffect(() => {
    applyWebDirection(locale);
  }, [locale]);

  // Correct I18nManager.isRTL on every launch: forceRTL persists across restarts
  // so a stale 'true' from a prior Arabic session would flip all layouts to RTL.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const shouldBeRTL = locale === 'ar';
    if (I18nManager.isRTL !== shouldBeRTL) {
      I18nManager.allowRTL(shouldBeRTL);
      I18nManager.forceRTL(shouldBeRTL);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLocale = useCallback(
    async (newLocale: SupportedLocale) => {
      await i18n.changeLanguage(newLocale);
      await persistLocalePreference(newLocale);

      if (Platform.OS === 'web') {
        applyWebDirection(newLocale);
        return;
      }

      const shouldBeRTL = newLocale === 'ar';
      const rtlChanged = I18nManager.isRTL !== shouldBeRTL;
      if (rtlChanged) {
        I18nManager.allowRTL(shouldBeRTL);
        I18nManager.forceRTL(shouldBeRTL);
        // Both Android and iOS keep already-mounted views in their old layout
        // direction until the JS bundle reloads, so trigger one whenever the
        // direction actually flipped. Show the branded "Switching…" transition
        // first (and let it paint) so the ~1s reload reads as applying the
        // user's choice instead of a bare black flash.
        showLocaleSwitchOverlay(newLocale);
        await waitForLocaleSwitchOverlay();
        await reloadNativeApp();
      }
    },
    [i18n],
  );

  return { locale, isRTL, changeLocale };
}
